import type { PaintOptions } from '../brush/types';
import { findChannel } from '../core/channels';
import type { GradientSettings } from '../core/gradient';
import { findLayerById, isSurfaceLayer } from '../core/layerTree';
import type {
  ChannelRef,
  DocumentMeta,
  PaintTargetKind,
  Point,
  RGBA,
  ToolId,
  ToolOptions,
  ViewState,
} from '../core/types';
import type { Engine } from '../engine/engine';
import type { StrokeTargetKind } from '../engine/strokeTarget';

export interface ToolPointerEvent {
  /** Position in document pixels, pulled onto a snap target where one applies. */
  point: Point;
  /**
   * Position in document pixels as the pointer actually is. Hit tests use this,
   * so a snap target next to a handle cannot steal the grab.
   */
  rawPoint: Point;
  /** Position in canvas-relative CSS pixels. */
  screen: Point;
  /** 0..1; 1 for devices that do not report real pressure. */
  pressure: number;
  /** `pen` is the only source of meaningful pressure values. */
  pointerType: 'mouse' | 'pen' | 'touch';
  button: number;
  shift: boolean;
  alt: boolean;
  ctrl: boolean;
}

export interface ToolContext {
  engine: Engine;
  doc: DocumentMeta;
  view: ViewState;
  options: ToolOptions;
  /** Brush settings of the active tool. */
  paint: PaintOptions;
  /** Whether a stroke should land on the layer or on its mask. */
  paintTarget: PaintTargetKind;
  /** Channel selected in the Channels panel; it overrides `paintTarget`. */
  activeChannel: ChannelRef;
  gradient: GradientSettings;
  foreground: RGBA;
  background: RGBA;
  requestRender: () => void;
  setStatus: (text: string) => void;
}

export interface OverlayContext {
  ctx: CanvasRenderingContext2D;
  toScreen: (point: Point) => Point;
  zoom: number;
}

/**
 * Tools are long-lived singletons that keep their own interaction state. The
 * canvas view is the only caller; it converts DOM events into `ToolPointerEvent`.
 */
export interface Tool {
  id: ToolId;
  /** CSS cursor while the tool is active. */
  cursor: string;
  /** Called when the tool becomes active, so it can set up an editing session. */
  activate?: (ctx: ToolContext) => void;
  onPointerDown?: (event: ToolPointerEvent, ctx: ToolContext) => void;
  onPointerMove?: (event: ToolPointerEvent, ctx: ToolContext) => void;
  onPointerUp?: (event: ToolPointerEvent, ctx: ToolContext) => void;
  onDoubleClick?: (event: ToolPointerEvent, ctx: ToolContext) => void;
  /** Return true when the key was consumed. */
  onKeyDown?: (event: KeyboardEvent, ctx: ToolContext) => boolean;
  /** Escape, tool switch or document switch. */
  cancel?: (ctx: ToolContext) => void;
  drawOverlay?: (overlay: OverlayContext, ctx: ToolContext) => void;
  /**
   * True for tools where the pointer is the geometry being placed, such as the
   * marquees and the shape tools. Those get their pointer snapped to guides and
   * to the grid; a brush never does, or the stroke would leave the cursor.
   */
  snaps?: boolean;
  /** Extra hint shown in the status bar. */
  hint?: string;
}

/** The surface a paint stroke may land on: groups and locks are not paintable. */
export function paintableLayer(doc: DocumentMeta): string | null {
  const layer = findLayerById(doc.layers, doc.activeLayerId);
  if (!layer || !isSurfaceLayer(layer) || layer.locked || layer.lockPixels) return null;
  return layer.id;
}

export interface PaintDestination {
  /** Layer id for a surface or mask stroke, channel id for a channel stroke. */
  id: string;
  kind: StrokeTargetKind;
  colorChannel: number | null;
}

/**
 * Where a stroke goes. A selected channel wins over the options bar, exactly as
 * in Photopea: picking a channel row is how you say what you are editing. Only
 * with the composite selected does the mask target decide between the layer's
 * mask and its pixels.
 */
export function paintDestination(
  doc: DocumentMeta,
  target: PaintTargetKind,
  channel: ChannelRef = { kind: 'composite' },
): PaintDestination | null {
  if (channel.kind === 'alpha' && findChannel(doc, channel.id)) {
    return { id: channel.id, kind: 'channel', colorChannel: null };
  }

  const layer = findLayerById(doc.layers, doc.activeLayerId);
  if (!layer || layer.locked) return null;

  const wantsMask =
    channel.kind === 'mask' ? channel.layerId === layer.id : channel.kind === 'composite' && target === 'mask';
  if (wantsMask && layer.hasMask) {
    if (layer.lockMask) return null;
    return { id: layer.id, kind: 'mask', colorChannel: null };
  }
  if (!isSurfaceLayer(layer) || layer.lockPixels) return null;

  const colorChannel = channel.kind === 'color' ? channel.index : null;
  return { id: layer.id, kind: 'surface', colorChannel };
}

/** The surface a move or a transform may warp. */
export function movableLayer(doc: DocumentMeta): string | null {
  const layer = findLayerById(doc.layers, doc.activeLayerId);
  if (!layer || !isSurfaceLayer(layer) || layer.locked || layer.lockPosition) return null;
  return layer.id;
}
